/**
 * Synchronous readers for cached os-settings in localStorage.
 *
 * Used by OsProvider before paint so launch behaviour does not wait on React hooks.
 */

import type {
  OsSettingsRequest,
  OsSettingsResponse,
} from "@/services/api/os-settings/types";

const STORAGE_KEY_PREFIX = "ani.os-settings.";

interface CachedOsSettings {
  ownerUserId: string;
  settings: OsSettingsRequest;
}

function storageKey(userId: string): string {
  return `${STORAGE_KEY_PREFIX}${userId}`;
}

/**
 * Write os-settings to the per-user localStorage cache.
 */
export function writeCachedOsSettings(
  userId: string,
  settings: OsSettingsRequest,
): void {
  if (typeof window === "undefined") return;
  try {
    const payload: CachedOsSettings = { ownerUserId: userId, settings };
    localStorage.setItem(storageKey(userId), JSON.stringify(payload));
  } catch {
    // ignore quota / serialization errors
  }
}

/**
 * Read cached os-settings for a user from localStorage.
 *
 * @param userId - Authenticated user id
 * @returns Cached settings or null when missing/invalid
 */
export function readCachedOsSettings(userId: string): OsSettingsRequest | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedOsSettings;
    if (parsed.ownerUserId !== userId) return null;
    return parsed.settings;
  } catch {
    return null;
  }
}

/**
 * Whether the user has opted in to reopening windows on launch.
 *
 * @param userId - Authenticated user id (null/undefined => false)
 * @returns true when reopenWindowsOnLaunch is enabled in cached os-settings
 */
export function readReopenWindowsOnLaunch(userId: string | null | undefined): boolean {
  if (!userId) return false;
  const settings = readCachedOsSettings(userId);
  return settings?.reopenWindowsOnLaunch === true;
}

/**
 * Whether new aniOS windows should open maximized by default.
 *
 * @param userId - Authenticated user id (null/undefined => false)
 * @returns true when openWindowsMaximized is enabled in cached os-settings
 */
export function readOpenWindowsMaximized(userId: string | null | undefined): boolean {
  if (!userId) return false;
  const settings = readCachedOsSettings(userId);
  return settings?.openWindowsMaximized === true;
}

/**
 * Merge GET /os-settings into localStorage so sync readers match the server.
 */
export function writeCachedOsSettingsFromServerResponse(
  userId: string,
  server: Pick<
    OsSettingsResponse,
    "pinnedAppIds" | "hiddenAppIds" | "reopenWindowsOnLaunch" | "openWindowsMaximized"
  >,
): void {
  const cached = readCachedOsSettings(userId);
  writeCachedOsSettings(userId, {
    pinnedAppIds: server.pinnedAppIds ?? cached?.pinnedAppIds ?? [],
    hiddenAppIds: server.hiddenAppIds ?? cached?.hiddenAppIds ?? [],
    reopenWindowsOnLaunch: server.reopenWindowsOnLaunch === true,
    openWindowsMaximized: server.openWindowsMaximized === true,
  });
}
